import { HeroCardButtonShape, HeroCardShape } from '../store/schema/HeroCardShape';
import {
    createLinkPreviewBorder,
    LinkPreviewType,
} from 'owa-link-preview/lib/utils/BaseLinkPreviewObject';
import {
    createDomElementsForWebpageLinkPreview,
    WebpageLinkPreviewObject,
} from 'owa-link-preview/lib/utils/WebpageLinkPreviewObject';
import { trace } from 'owa-trace';

const MAX_IMAGE_WIDTH = '400px';
const OPEN_URL_TYPE = 'openUrl';

// Hack to temporarily render hero cards as link preview objects
function renderHeroCardAsLinkPreview(heroCard: HeroCardShape): HTMLElement {
    if (!heroCard) {
        trace.warn('[renderHeroCardAsLinkPreview] Argument null: heroCard undefined');
        return document.createElement('span');
    }

    const linkPreview: WebpageLinkPreviewObject = {
        type: LinkPreviewType.WebpagePreview,
        description: heroCard.text,
        title: heroCard.title,
        url: getUrlFromHeroCard(heroCard),
        thumbnailImageUrl: heroCard.images ? heroCard.images[0] : null,
    };

    createLinkPreviewBorder('CT', linkPreview);
    createDomElementsForWebpageLinkPreview(linkPreview, true);
    if (linkPreview.thumbnailImageElement) {
        linkPreview.thumbnailImageElement.style.display = 'block';
    }

    linkPreview.table.style.margin = '5px';
    return linkPreview.table;
}

// Render the card as just an image that is potentially wrapped in an anchor tag if we have
// a usable URL
function renderHeroCardAsImage(heroCard: HeroCardShape): HTMLElement {
    const imageDiv = document.createElement('div') as HTMLDivElement;
    const image = document.createElement('img') as HTMLImageElement;
    const url = getUrlFromHeroCard(heroCard);
    image.style.maxWidth = MAX_IMAGE_WIDTH;
    image.src = heroCard.images[0];

    if (url) {
        const imageAnchor = document.createElement('a') as HTMLAnchorElement;
        imageAnchor.href = url;
        imageAnchor.target = '_blank';
        imageAnchor.appendChild(image);
        imageDiv.appendChild(imageAnchor);
    } else {
        imageDiv.appendChild(image);
    }

    return imageDiv;
}

// Try to extract a useful url from the hero card:
// - If it has a tap action with the openUrl type then we'll use that url.
// - Otherwise if it has a url value set then use that
// - Otherwise if it has buttons then get the url from the first button with an openUrl type. This fallback isn't ideal, but it will
//   serve as a stopgap until we figure out a more longterm plan for detailing with the cards returned by Cortana
function getUrlFromHeroCard(heroCard: HeroCardShape): string {
    let url: string;
    if (heroCard.tapAction && heroCard.tapAction.type === OPEN_URL_TYPE) {
        url = heroCard.tapAction.value;
    } else if (heroCard.url) {
        url = heroCard.url;
    } else if (heroCard.buttons) {
        const matchingButton: HeroCardButtonShape[] = heroCard.buttons.filter(
            value => value.type === OPEN_URL_TYPE
        );
        if (matchingButton && matchingButton.length > 0) {
            url = matchingButton[0].value;
        }
    }

    return url;
}

export default function renderHeroCardToHtml(
    heroCard: HeroCardShape,
    includeButtons: boolean
): HTMLElement {
    // If we have a description, render it like a link preview.
    // If we have no description, but we do have an image, render it as just an image
    // If we have neither a description nor an image then we can't render anything useful. This is unexpected and shouldn't happen.
    if (heroCard) {
        if (heroCard.text) {
            return renderHeroCardAsLinkPreview(heroCard);
        } else if (heroCard.images && heroCard.images[0]) {
            return renderHeroCardAsImage(heroCard);
        }
    }

    trace.warn(
        '[renderHeroCardToHtml] Card contains no description or image, so returning empty span'
    );
    return document.createElement('span');
}
